import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Logo } from '@/components/brand/logo';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { matchTarget, navTargets, type NavItem } from './navigation';

/**
 * Barra lateral (§19): recolhível no desktop (fica só com os ícones), gaveta no celular.
 * `context` é o que vai abaixo do logo — a empresa ativa ou o selo do painel da plataforma.
 */
export function AppSidebar({
  home,
  items,
  context,
}: {
  home: string;
  items: NavItem[];
  context: ReactNode;
}) {
  const { state, isMobile, setOpenMobile } = useSidebar();
  const { pathname } = useLocation();
  const active = matchTarget(navTargets(items), pathname);
  const collapsed = state === 'collapsed' && !isMobile;
  // No celular a gaveta cobre a tela: fecha ao escolher um destino.
  const close = () => setOpenMobile(false);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="gap-3">
        <Link to={home} aria-label="troig — início" onClick={close} className="flex h-8 px-1">
          {collapsed ? <Logo variant="symbol" className="h-8" /> : <Logo className="h-8" />}
        </Link>
        {context}
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {items.map((item) => {
              if (item.kind === 'link') {
                return (
                  <SidebarMenuItem key={item.label}>
                    <SidebarMenuButton
                      asChild
                      isActive={active?.to === item.to}
                      tooltip={item.label}
                    >
                      <NavLink to={item.to} onClick={close}>
                        <item.icon aria-hidden />
                        <span>{item.label}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              }
              if (item.kind === 'group') {
                return (
                  <NavGroup key={item.label} item={item} activeTo={active?.to} onNavigate={close} />
                );
              }
              return (
                <SidebarMenuItem key={item.label}>
                  {/* Não navega nem finge ação: mostra a direção do produto. O selo e o rótulo
                      acessível dizem que ainda não existe. */}
                  <SidebarMenuButton
                    aria-label={`${item.label}, em breve`}
                    tooltip={`${item.label} — em breve. ${item.description}`}
                    className="cursor-default text-muted-foreground hover:bg-transparent hover:text-muted-foreground"
                  >
                    <item.icon aria-hidden />
                    <span>{item.label}</span>
                  </SidebarMenuButton>
                  <SidebarMenuBadge className="text-xs font-normal text-muted-foreground">
                    em breve
                  </SidebarMenuBadge>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarRail />
    </Sidebar>
  );
}

function NavGroup({
  item,
  activeTo,
  onNavigate,
}: {
  item: Extract<NavItem, { kind: 'group' }>;
  activeTo: string | undefined;
  onNavigate: () => void;
}) {
  const { state, setOpen, isMobile } = useSidebar();
  const containsActive = item.children.some((child) => child.to === activeTo);

  return (
    <Collapsible asChild defaultOpen={containsActive} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={item.label}
            isActive={containsActive && state === 'collapsed' && !isMobile}
            // Recolhida, a barra não tem onde mostrar o submenu: abre a barra primeiro.
            onClick={() => state === 'collapsed' && setOpen(true)}
          >
            <item.icon aria-hidden />
            <span>{item.label}</span>
            <ChevronRight
              aria-hidden
              className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90"
            />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.children.map((child) => (
              <SidebarMenuSubItem key={child.to}>
                <SidebarMenuSubButton asChild isActive={child.to === activeTo}>
                  <NavLink to={child.to} onClick={onNavigate}>
                    <span>{child.label}</span>
                  </NavLink>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}
